import { cn } from '@/utils/cn'

export interface WelcomeScreenProps {
  onSelect: (text: string) => void
  disabled?: boolean
  className?: string
}

// Static starter packs for the empty state. Deliberately small and hardcoded —
// there is no context to be smart about before the first message, so this is a
// curated set, not a generated one. Contextual chips take over after turn one.
const STARTER_PACKS: { label: string; prompts: string[] }[] = [
  {
    label: 'Coverage',
    prompts: [
      'Is this procedure covered under my plan?',
      "What's my deductible so far?",
    ],
  },
  {
    label: 'Find care',
    prompts: [
      'Find an in-network doctor near me',
      'Is my current doctor in-network?',
    ],
  },
  {
    label: 'Cost',
    prompts: ['What will this cost me out of pocket?'],
  },
  {
    label: 'Get care',
    prompts: ['What are my next steps?', 'Talk to a care advocate'],
  },
]

/**
 * Empty-state welcome shown before any messages exist. Surfaces a few tappable
 * starter prompts so members aren't typing into a blank box.
 */
export function WelcomeScreen({
  onSelect,
  disabled = false,
  className,
}: WelcomeScreenProps) {
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-6 px-4 py-8',
        className,
      )}
    >
      <p className="max-w-sm text-center text-gray-600">
        Hi! I can help with coverage, finding care, and costs. Pick a starting
        point or just type your question.
      </p>

      <div className="w-full max-w-md space-y-4">
        {STARTER_PACKS.map((pack) => (
          <div key={pack.label}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {pack.label}
            </p>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label={`${pack.label} suggestions`}
            >
              {pack.prompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(prompt)}
                  className={cn(
                    'rounded-full border border-primary-200 bg-white px-3 py-1.5 text-sm text-primary-700',
                    'transition-colors hover:bg-primary-50',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
